import { Router, Route } from '@solidjs/router';
import { MetaProvider, Title, Meta, Link } from '@solidjs/meta';
import { Suspense, createEffect, lazy, type JSX } from 'solid-js';
import { isServer } from 'solid-js/web';
import { inject } from '@vercel/analytics';
import { authStore } from '@stores/session';
import './styles/app.css';

import AppShell from '@components/layout/AppShell';
import RouteErrorBoundary from '@components/layout/RouteErrorBoundary';

// Routes — split between eagerly-imported entry points (Home, SignIn,
// the strain-listing surfaces patients land on most frequently) and
// lazy-loaded everything else. Vinxi/Vite emits a separate chunk per
// `lazy()` import, so first paint on `/` no longer drags the admin
// console, OCR upload form, message threads, etc. through the wire.
//
// Trade-off: lazy routes incur a single network round-trip on first
// navigation. We accept that on heavy/seldom-used routes (admin tabs,
// COA upload). The hot landing surfaces stay synchronous.
import HomeRoute from '@routes/HomeRoute';
import SignInRoute from '@routes/SignInRoute';
import StrainListRoute from '@routes/StrainListRoute';
import StrainDetailRoute from '@routes/StrainDetailRoute';
import StockRoute from '@routes/StockRoute';

const FilterRoute        = lazy(() => import('@routes/FilterRoute'));
const TopRatedRoute      = lazy(() => import('@routes/TopRatedRoute'));
const DiaryRoute         = lazy(() => import('@routes/DiaryRoute'));
const StartSessionRoute  = lazy(() => import('@routes/StartSessionRoute'));
const COAUploadRoute     = lazy(() => import('@routes/COAUploadRoute'));
const COAIndexRoute      = lazy(() => import('@routes/COAIndexRoute'));
const ForumRoute         = lazy(() => import('@routes/ForumRoute'));
const ForumPostRoute     = lazy(() => import('@routes/ForumPostRoute'));
const BlogRoute          = lazy(() => import('@routes/BlogRoute'));
const BlogPostRoute      = lazy(() => import('@routes/BlogPostRoute'));
const PortfolioRoute     = lazy(() => import('@routes/PortfolioRoute'));
const SharedPortfolioRoute = lazy(() => import('@routes/SharedPortfolioRoute'));
const SharedScriptRoute    = lazy(() => import('@routes/SharedScriptRoute'));
const AdminRoute         = lazy(() => import('@routes/AdminRoute'));
const SettingsRoute      = lazy(() => import('@routes/SettingsRoute'));
const WatchlistRoute     = lazy(() => import('@routes/WatchlistRoute'));
const MessagesRoute      = lazy(() => import('@routes/MessagesRoute'));
const MessageThreadRoute = lazy(() => import('@routes/MessageThreadRoute'));
const TravelRoute        = lazy(() => import('@routes/TravelRoute'));

// Root SolidStart app. Wraps the whole tree in <MetaProvider> so any
// route can mount <Title>/<Meta> and have those values rendered into
// the initial SSR HTML (critical for social unfurling — the whole
// reason we migrated off the pure-client build).
export default function App(): JSX.Element {
  // Vercel Web Analytics. v2 of @vercel/analytics dropped the Solid-
  // specific entry point, so we use the framework-agnostic `inject()`
  // helper — it wires up the tracking beacon on the client (no-op on
  // SSR, guarded by isServer) and listens to SolidJS Router's history
  // changes automatically via the standard History API.
  //
  // Two reactive gates before injection:
  //   1. Wait until the auth store has settled so we can read the
  //      profile's role. Calling inject() and *then* deciding the user
  //      is an admin is too late — there's no teardown, and we'd skew
  //      data with one or two pageviews per admin session.
  //   2. Skip injection entirely for admins. We don't want our own
  //      moderation traffic showing up in the dashboard.
  let injected = false;
  createEffect(() => {
    if (isServer || injected) return;
    if (authStore.loading()) return;
    // Signed-in user but profile hasn't materialised yet — wait. Without
    // the profile we don't know the role and can't safely skip admins.
    if (authStore.user() && !authStore.profile()) return;
    if (authStore.isAdmin()) return;
    injected = true;
    // `framework: 'solid'` is a hint for Vercel's dashboard so it knows
    // to expect SolidJS Router conventions when grouping events.
    inject({ framework: 'solid' });
  });

  return (
    <MetaProvider>
      <Title>Terp Track &ndash; Dial in your relief</Title>
      <Meta charset="UTF-8" />
      <Meta
        name="viewport"
        content="width=device-width, initial-scale=1.0, viewport-fit=cover"
      />
      <Meta name="theme-color" content="#041815" />
      <Meta
        name="description"
        content="Terp Track &ndash; UK medical cannabis terpene intelligence. Track batch terpene profiles, vape temperatures, and symptom relief."
      />
      <Meta property="og:site_name" content="Terp Track" />
      <Meta property="og:type" content="website" />
      <Meta property="og:image" content="/og-default.png" />
      <Meta name="twitter:card" content="summary_large_image" />
      <Link rel="icon" type="image/png" sizes="32x32" href="/logo/logo-32.png" />
      <Link rel="icon" type="image/png" sizes="192x192" href="/logo/logo-192.png" />
      <Link rel="apple-touch-icon" sizes="192x192" href="/logo/logo-512px.png" />
      <Link rel="apple-touch-icon" sizes="512x512" href="/logo/logo-512px.png" />
      <Link rel="manifest" href="/manifest.webmanifest" />
      <Link rel="preconnect" href="https://fonts.googleapis.com" />
      <Link rel="preconnect" href="https://fonts.gstatic.com" crossorigin="anonymous" />
      <Link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Outfit:wght@500;600;700&display=swap"
      />
      <Router
        root={(props) => (
          <AppShell>
            {/* Stack order: ErrorBoundary → Suspense → children.
                ErrorBoundary catches sync render failures and resource-
                fetcher rejections so a single component crash doesn't
                black-hole the whole app — user gets a "Try again" card
                instead of a blank page. Suspense catches in-flight
                resource loads + lazy() chunk fetches and shows the
                Loading fallback (also the SSR signal that meta tags
                should wait for the route's data before flushing
                HTML — preserves social-unfurling behaviour). */}
            <RouteErrorBoundary>
              <Suspense fallback={<p class="px-4 sm:px-6 py-10 text-sm">Loading…</p>}>
                {props.children}
              </Suspense>
            </RouteErrorBoundary>
          </AppShell>
        )}
      >
        <Route path="/" component={HomeRoute} />
        <Route path="/sign-in" component={SignInRoute} />
        <Route path="/symptom-match" component={FilterRoute} />
        <Route path="/strains" component={StrainListRoute} />
        {/* Static /strains/top-rated must be declared BEFORE the :slug
            route so it isn't captured as a slug. */}
        <Route path="/strains/top-rated" component={TopRatedRoute} />
        <Route path="/strains/:slug" component={StrainDetailRoute} />
        <Route path="/diary" component={DiaryRoute} />
        <Route path="/diary/start" component={StartSessionRoute} />
        <Route path="/coa" component={COAIndexRoute} />
        <Route path="/coa/upload" component={COAUploadRoute} />
        <Route path="/community" component={ForumRoute} />
        <Route path="/forum" component={ForumRoute} />
        <Route path="/forum/:id" component={ForumPostRoute} />
        <Route path="/blog" component={BlogRoute} />
        <Route path="/blog/:slug" component={BlogPostRoute} />
        <Route path="/stock" component={StockRoute} />
        <Route path="/my-strains" component={PortfolioRoute} />
        <Route path="/shared/:token" component={SharedPortfolioRoute} />
        <Route path="/shared-script" component={SharedScriptRoute} />
        <Route path="/watchlist" component={WatchlistRoute} />
        <Route path="/admin" component={AdminRoute} />
        <Route path="/settings" component={SettingsRoute} />
        <Route path="/messages" component={MessagesRoute} />
        <Route path="/messages/:id" component={MessageThreadRoute} />
        <Route path="/travel" component={TravelRoute} />
      </Router>
    </MetaProvider>
  );
}
